// Loads the library, then switches between screens: home, the library, and an episode. An episode opens in one of three
// views (Quick, Script, Timeline), chosen inside TimelineEditor; a Quick episode opens in Quick on its first new scene, a full one in Script.
import { lazy, Suspense, useEffect, useState } from "react";
import { preloadImages } from "../library/images";
import { setLibrary } from "../library/store";
import { api } from "./api";
import { onNotice } from "./jobs";
import { Page, Phone, Toast } from "./common";
import { TimelineLoader } from "./timeline/TimelineLoader";
import { Home } from "./Home";
import { Landing } from "./landing/Landing";
import { CrashScreen } from "./CrashScreen";
import { hasStarted, markStarted, account, loadAccount } from "./account";
import { TimelineEditor } from "./timeline/TimelineEditor";
import { LibraryPage } from "./library/LibraryPage";
import { saveView } from "./timeline/EditorShell";
import { startWithScene } from "./quick/QuickView";
import "../index.css";

const DevCanvas = lazy(() => import("./dev/Canvas"));

type Screen = { name: "home" } | { name: "welcome" } | { name: "canvas" } | { name: "library" } | { name: "episode"; id: string };

const fromHash = (): Screen => {
  const m = location.hash.match(/^#\/ep\/([a-z0-9-]+)/i);
  if (m) return { name: "episode", id: m[1]! };
  if (location.hash === "#/dev/canvas") return { name: "canvas" };
  if (location.hash === "#/welcome") return { name: "welcome" };
  if (location.hash === "#/library") return { name: "library" };
  return { name: "home" };
};
const hashOf = (s: Screen) => (s.name === "episode" ? `#/ep/${s.id}` : s.name === "home" ? "#/" : s.name === "welcome" ? "#/welcome" : s.name === "canvas" ? "#/dev/canvas" : `#/${s.name}`);

export function App() {
  const [screen, setScreenState] = useState<Screen>(fromHash);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  // Work that fails after its page has gone (painting, animating, a voice) says so here, on any screen.
  useEffect(() => onNotice(setError), []);
  useEffect(() => {
    // Who it is first (on Cloudflare this also gives a new visitor their guest id), then the library.
    loadAccount().then(() => api.library()).then(lib => { setLibrary(lib); void preloadImages(lib).catch(() => {}); }, e => setError(`Couldn't load the library: ${(e as Error).message}`))
      .finally(() => setReady(true));
  }, []);

  const go = (s: Screen) => {
    setScreenState(s);
    history.replaceState(null, "", hashOf(s));
  };
  useEffect(() => {
    const onHash = () => setScreenState(fromHash());
    addEventListener("hashchange", onHash);
    return () => removeEventListener("hashchange", onHash);
  }, []);

  // A new episode is a timeline (v6) episode. The quick way opens in Quick view on its first new scene; the full way in Script.
  async function create(quick: boolean) {
    try {
      const e = await api.create();
      saveView(e.id, quick ? "quick" : "script");
      if (quick) startWithScene(e.id); // straight into its first scene
      go({ name: "episode", id: e.id });
    } catch (e) { setError((e as Error).message); }
  }

  // "Make your own" in the demo, and "Get started" on the landing page: into the app.
  async function own() {
    markStarted();
    try {
      const e = await api.create();
      saveView(e.id, "quick");
      startWithScene(e.id);
      go({ name: "episode", id: e.id });
    } catch (e) { setError((e as Error).message); }
  }
  const start = () => { markStarted(); go({ name: "home" }); };

  // A bird's-eye view of every screen, drawn from real data; it loads its own, so it needs nothing from here.
  if (screen.name === "canvas") return <Suspense fallback={null}><DevCanvas /></Suspense>;
  if (!ready) return <Phone><Page><div className="loading">Loading…</div></Page></Phone>;

  return (
    <Phone>
      <CrashScreen key={hashOf(screen)}>
        {screen.name === "welcome" || (screen.name === "home" && !account() && !hasStarted())
          ? <Landing onOwn={() => void own()} onStart={start} onSignedIn={() => go({ name: "home" })} /> : null}
        {screen.name === "home" && (account() || hasStarted()) && <Home onSignOut={() => go({ name: "welcome" })} onQuick={() => void create(true)} onNew={() => void create(false)} onOpen={id => go({ name: "episode", id })} onLibrary={() => go({ name: "library" })} />}
        {screen.name === "library" && <LibraryPage onBack={() => go({ name: "home" })} />}
        {screen.name === "episode" && (
          <TimelineLoader key={screen.id} id={screen.id} editor={TimelineEditor} onOwn={() => void own()} onBack={() => go({ name: "home" })} onError={m => { setError(m); go({ name: "home" }); }} />
        )}
      </CrashScreen>
      {error && <Toast onClick={() => setError(null)}>{error}</Toast>}
    </Phone>
  );
}

export default App;
